import { Box, SwipeableDrawer, Divider } from '@mui/material'
import FormModalHeading from 'components/generic/modals/form-modal/FormModalHeading'
import { noop } from 'lodash'
import BasicDataGridV2 from 'components/generic/datagrid'
import { useMemo, useState } from 'react'
import { Link } from '@mui/material_modern'
import { useGetCustomerMaterialPurchasePriceHistoryQuery } from 'services/customers'
import { SORTABLE_PURCHASE_PRICE_HISTORY_FIELDS } from 'utils/constants'
import { generateRenderHeaderFunction } from 'utils/generateRenderHeaderFunction'
import Dinero from 'dinero.js'
import { setDecimalPrecision } from 'utils/helpers'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'
import { dateFormatter } from 'utils/data-manipulation/valueFormatter'

interface PurchasePriceHistoryQueryOptions {
  filter: {
    sort: {
      field: string
      ascending: boolean
    }
  }
  page: number
  pageSize: number
}

interface CustomerMaterialPair {
  customerId: string
  materialTypeId: string
}

export interface PurchasePriceHistoryBladeProps {
  openCondition: boolean
  onCloseFunction: () => void
  customerMaterialPair: CustomerMaterialPair
  useBackendSortAndFilter?: boolean
}

const PurchasePriceHistoryBlade: React.FC<PurchasePriceHistoryBladeProps> = ({
  openCondition,
  onCloseFunction,
  customerMaterialPair,
  useBackendSortAndFilter = true,
}) => {
  const { defaultYardUm } = useAppSelector(state => state.settings)
  const [queryOptions, setQueryOptions] = useState<PurchasePriceHistoryQueryOptions>({
    filter: {
      sort: {
        field: 'date',
        ascending: false,
      },
    },
    page: 1,
    pageSize: 10,
  })

  const { data: priceHistoryRows, isFetching: isLoadingPriceHistory } = useGetCustomerMaterialPurchasePriceHistoryQuery(
    {
      ...queryOptions,
      filter: JSON.stringify(queryOptions.filter),
      customerId: customerMaterialPair.customerId,
      materialTypeId: customerMaterialPair.materialTypeId,
    },
    { skip: !customerMaterialPair.customerId || !customerMaterialPair.materialTypeId },
  )

  const transformedPriceHistoryRows = useMemo(() => {
    return (priceHistoryRows?.Items || []).map(item => ({
      id: item.id,
      yardName: item.yardName?.toUpperCase(),
      inboundTicketNumber: item.inboundTicketNumber,
      inboundTicketId: item.inboundTicketId,
      date: item.date ? dateFormatter(item.date) : '',
      materialName: item.materialName,
      netWeight: item.materialStatus?.netWeight + defaultYardUm,
      weightAndPrice: `${setDecimalPrecision(Dinero({ ...item.materialStatus.weightAndPrice }).toUnit())}/${item.materialStatus.weightAndPrice.um}`,
    }))
  }, [priceHistoryRows])

  const setFilter = newFilter => {
    setQueryOptions(prevState => ({ ...prevState, filter: newFilter }))
  }

  const renderHeaderWithSort = useMemo(
    () => generateRenderHeaderFunction(queryOptions.filter, setFilter),
    [queryOptions.filter],
  )

  const getPriceHistoryColumns = renderHeaderWithSort => {
    return [
      {
        field: 'yardName',
        headerName: 'Yard',
        flex: 1,
        sortable: false,
      },
      {
        field: 'inboundTicketNumber',
        headerName: 'Ticket #',
        flex: 1,
        sortable: false,
        renderCell: params => (
          <Link
            href={`/app/purchases/ticket-library/${params.row.inboundTicketId}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={theme => ({
              color: theme.palette.primary.main,
              '&:hover': {
                textDecoration: 'underline',
                color: theme.palette.primary.dark,
                cursor: 'pointer',
              },
            })}
          >
            {params.row.inboundTicketNumber}
          </Link>
        ),
      },
      {
        field: 'date',
        headerName: 'Effective Date',
        flex: 1,
        sortable: false,
        renderHeader: () =>
          renderHeaderWithSort('Effective Date', SORTABLE_PURCHASE_PRICE_HISTORY_FIELDS.EFFECTIVE_DATE, {
            startAscending: false,
          }),
      },
      {
        field: 'materialName',
        headerName: 'Material',
        flex: 1,
        sortable: false,
      },
      {
        field: 'netWeight',
        headerName: 'Weight',
        flex: 1,
        sortable: false,
      },
      {
        field: 'weightAndPrice',
        headerName: 'Price',
        flex: 1,
        sortable: false,
        renderHeader: () => renderHeaderWithSort('Price', SORTABLE_PURCHASE_PRICE_HISTORY_FIELDS.PRICE),
      },
    ]
  }

  return (
    <>
      <SwipeableDrawer
        anchor="right"
        open={openCondition}
        onOpen={noop}
        onClose={onCloseFunction}
        PaperProps={{ sx: { maxWidth: '1024px', width: '100%' } }}
        disableSwipeToOpen
      >
        <FormModalHeading title="Price History" backButtonPosition="right" onBack={onCloseFunction} />
        <Divider />
        <Box flexGrow={1}>
          <BasicDataGridV2
            rows={transformedPriceHistoryRows}
            columns={getPriceHistoryColumns(renderHeaderWithSort)}
            pagination
            paginationMode="server"
            disableColumnMenu={useBackendSortAndFilter}
            pageSize={queryOptions.pageSize}
            useBackendSortAndFilter
            filter={queryOptions?.filter}
            setFilter={newFilter => setQueryOptions(prevState => ({ ...prevState, filter: newFilter }))}
            rowCount={priceHistoryRows?.totalResultsCount || 0}
            onPageChange={newPageNumber => setQueryOptions(prevState => ({ ...prevState, page: newPageNumber + 1 }))}
            onPageSizeChange={newPageSize => setQueryOptions(prevState => ({ ...prevState, pageSize: newPageSize }))}
            setQueryOptions={setQueryOptions}
            autoPageSize={false}
            loading={isLoadingPriceHistory}
          />
        </Box>
      </SwipeableDrawer>
    </>
  )
}

export default PurchasePriceHistoryBlade
